import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  CollectionPreferences,
  Pagination,
  Table,
} from "@amzn/awsui-components-react/polaris";

import { RootState } from "../../app/rootReducer";
import {
  FullAsinWithAudits,
  RestrictedAsin,
} from "../../../from-server/asin-central/model";
import {
  fetchEditableAttributes,
  fetchPorAsins,
} from "../../porAsinList/porAsinListSlice";
import {
  setSort,
  setValidationPageNumber,
} from "../asinValidationTableConnector";
import { trackComponentView } from "../../telemetry/telemetry";
import { SortFacet } from "../../../from-server/asin-central/search";
import { asinTableColumnDefinitions } from "../asinValidationTable-config";
import {
  EmptyTableState,
  defaultSort,
} from "../../porAsinList/TableCommonElements";
import { pageSelectorOptions } from "../../porAsinPageControl/porAsinPageControl";
import { collectPropertyKeys } from "../../searchSideBar/propertyFilterKeys";
import { isNoFilterApplied } from "../../searchSideBar/queryStringHelpers";
import { getEditValidationColumnDefs } from "../validationListEdit-config";
import {
  DASHBOARD_COLUMN_DEFINITIONS,
  DEFAULT_VISIBLE_COLUMNS,
} from "./columnDefinitions";
import {
  DEFAULT_PREFERENCES,
  readInitialPreferences,
  writePreferences,
  ValidationDashboardTablePreferences,
} from "./preferences";
import { ValidationDashboardTableHeader } from "./ValidationDashboardTableHeader";
import { SubmitPassExceptionsModal } from "./SubmitPassExceptionsModal";
import { SubmitCatalogDefaultsModal } from "./SubmitCatalogDefaultsModal";

// Chart-driven chip whose column doesn't need to auto-reveal: the
// Status Overview bar already surfaces the value, and revealing the
// column would clutter the table without adding new information.
const AUTO_REVEAL_EXCLUDED_KEYS: ReadonlySet<string> = new Set([
  "validationOverallStatus",
]);

function mergeActiveColumns(
  visibleContent: readonly string[],
  activeColumns: readonly string[]
): readonly string[] {
  if (activeColumns.length === 0) {
    return visibleContent;
  }
  const merged = new Set<string>(visibleContent);
  let changed = false;
  for (const col of activeColumns) {
    if (!merged.has(col)) {
      merged.add(col);
      changed = true;
    }
  }
  return changed ? Array.from(merged) : visibleContent;
}

function isFullAsin(
  entry: FullAsinWithAudits | RestrictedAsin
): entry is FullAsinWithAudits {
  return !("isRestricted" in entry) || entry.isRestricted !== true;
}

function porRowKey(entry: FullAsinWithAudits): string {
  return `${entry.asin},${entry.marketplace}`;
}

export const ValidationDashboardTable: React.FC = () => {
  const dispatch = useDispatch();

  const currentPagePorAsins = useSelector(
    (state: RootState) => state.porAsinList.currentPagePorAsins
  );
  const count = useSelector((state: RootState) => state.porAsinList.count);
  const isLoading = useSelector(
    (state: RootState) => state.porAsinList.isLoading
  );
  const queryString = useSelector(
    (state: RootState) => state.generateQuery.queryString
  );
  const queryPropertyFilters = useSelector(
    (state: RootState) => state.generateQuery.queryPropertyFilters
  );
  const pageNumber = useSelector(
    (state: RootState) => state.asinValidationTableControl.pageNumber
  );
  const sort = useSelector(
    (state: RootState) => state.asinValidationTableControl.sort
  );
  const descending = useSelector(
    (state: RootState) => state.asinValidationTableControl.descending
  );
  const activePropertyFiltersVisibilityEnabled = useSelector(
    (state: RootState) =>
      state.generateQuery.activePropertyFiltersVisibilityEnabled
  );

  const [preferences, setPreferences] =
    useState<ValidationDashboardTablePreferences>(() =>
      readInitialPreferences()
    );

  const [selectedAsins, setSelectedAsins] = useState<FullAsinWithAudits[]>([]);

  const sortFacets: SortFacet[] = useMemo(
    () =>
      sort === undefined
        ? defaultSort
        : [
            {
              attributeName: sort.sortingField,
              ascending: !descending,
            },
          ],
    [sort, descending]
  );

  const editableAttributes = useSelector(
    (state: RootState) => state.porAsinList.editableAttributes
  );
  const editableAttributesLoaded = useSelector(
    (state: RootState) => state.porAsinList.editableAttributesLoaded
  );
  const isGettingEditableAttributes = useSelector(
    (state: RootState) => state.porAsinList.isGettingEditableAttributes
  );
  const isEditing = useSelector(
    (state: RootState) => state.asinValidationTableControl.isEditing
  );
  const editableValidationAsins = useSelector(
    (state: RootState) =>
      state.asinValidationTableControl.editableValidationAsins
  );
  const selectedValidationService = useSelector(
    (state: RootState) =>
      state.asinValidationTableControl.selectedValidationService
  );
  const refreshFlipper = useSelector(
    (state: RootState) => state.asinValidationTableControl.refreshFlipper
  );

  useEffect(() => {
    trackComponentView("validationDashboardTable", "table");
  }, []);

  // Gate on `editableAttributesLoaded` (set to true on both success
  // and failure) so a failed fetch doesn't leave an empty-list +
  // not-in-flight state that would re-fire this effect every render.
  useEffect(() => {
    if (!editableAttributesLoaded && !isGettingEditableAttributes) {
      dispatch(fetchEditableAttributes());
    }
  }, [dispatch, editableAttributesLoaded, isGettingEditableAttributes]);

  // Track whether the current fetch is a "refresh" (triggered by
  // `refreshFlipper` after a save/submit) versus a user-driven query
  // change. On user-driven changes we want to clear prior notifications
  // (e.g. the Restricted-ASINs banner from a previous query). On a
  // post-submit refresh we want to preserve the success notification
  // that was just dispatched.
  const prevRefreshFlipperRef = useRef(refreshFlipper);

  useEffect(() => {
    // Sync the ref before the early return so a flipper toggle that
    // fires while no filter is applied (e.g. a submit/refresh on an
    // empty query) still updates the tracked value. Otherwise the
    // next genuine user-driven query change would see a stale
    // "flipper changed" reading and skip the notification clear.
    const isRefreshFlip = prevRefreshFlipperRef.current !== refreshFlipper;
    prevRefreshFlipperRef.current = refreshFlipper;
    if (isNoFilterApplied(queryString)) {
      return;
    }
    const offset = preferences.pageSize * (pageNumber - 1);
    dispatch(
      fetchPorAsins(
        offset,
        preferences.pageSize,
        queryString as string,
        sortFacets,
        !isRefreshFlip
      )
    );
  }, [
    dispatch,
    pageNumber,
    preferences.pageSize,
    queryString,
    sortFacets,
    refreshFlipper,
  ]);

  const activeColumns = useMemo<readonly string[]>(() => {
    const keys = new Set<string>();
    collectPropertyKeys(queryPropertyFilters, keys, {
      exclude: AUTO_REVEAL_EXCLUDED_KEYS,
    });
    return Array.from(keys);
  }, [queryPropertyFilters]);

  useEffect(() => {
    if (!activePropertyFiltersVisibilityEnabled) {
      return;
    }
    if (activeColumns.length === 0) {
      return;
    }
    setPreferences((prev: ValidationDashboardTablePreferences) => {
      const nextVisible = mergeActiveColumns(
        prev.visibleContent,
        activeColumns
      );
      if (nextVisible === prev.visibleContent) {
        return prev;
      }
      const next: ValidationDashboardTablePreferences = {
        pageSize: prev.pageSize,
        visibleContent: nextVisible,
      };
      writePreferences(next);
      return next;
    });
  }, [activeColumns, activePropertyFiltersVisibilityEnabled]);

  const onPreferencesConfirm = useCallback(
    (event: {
      readonly detail: {
        readonly pageSize?: number;
        readonly visibleContent?: ReadonlyArray<string>;
      };
    }) => {
      const next: ValidationDashboardTablePreferences = {
        pageSize:
          typeof event.detail.pageSize === "number"
            ? event.detail.pageSize
            : DEFAULT_PREFERENCES.pageSize,
        visibleContent:
          event.detail.visibleContent === undefined
            ? DEFAULT_VISIBLE_COLUMNS
            : Array.from(event.detail.visibleContent),
      };
      writePreferences(next);
      setPreferences(next);
      dispatch(setValidationPageNumber({ pageNumber: 1 }));
    },
    [dispatch]
  );

  const pagesCount = useMemo(
    () =>
      preferences.pageSize > 0
        ? Math.max(1, Math.ceil(count / preferences.pageSize))
        : 1,
    [count, preferences.pageSize]
  );

  // Drop stale selections when the current page or query changes so
  // the header counter reflects only currently visible selections.
  useEffect(() => {
    if (selectedAsins.length === 0) {
      return;
    }
    const visibleKeys = new Set(
      currentPagePorAsins.filter(isFullAsin).map(porRowKey)
    );
    const filtered = selectedAsins.filter((item) =>
      visibleKeys.has(porRowKey(item))
    );
    if (filtered.length !== selectedAsins.length) {
      setSelectedAsins(filtered);
    }
  }, [currentPagePorAsins, selectedAsins]);

  const columnDefinitions = isEditing
    ? getEditValidationColumnDefs(
        editableAttributes,
        selectedValidationService?.overallAttributeName ?? ""
      )
    : DASHBOARD_COLUMN_DEFINITIONS;

  const items = isEditing
    ? (editableValidationAsins as FullAsinWithAudits[])
    : (currentPagePorAsins as FullAsinWithAudits[]);

  // While editing, ensure the column for the service being validated
  // is visible even when the user's saved preferences exclude it
  // (manual validation columns are hidden by default). This override
  // is transient — saved preferences are untouched.
  const visibleColumns = useMemo<readonly string[]>(() => {
    const base = preferences.visibleContent;
    if (!isEditing) {
      return base;
    }
    const editingColumnId = selectedValidationService?.overallAttributeName;
    if (!editingColumnId || base.includes(editingColumnId)) {
      return base;
    }
    return [...base, editingColumnId];
  }, [preferences.visibleContent, isEditing, selectedValidationService]);

  // Multi-select + Submit-buttons are meaningful only when reading the
  // match set. In edit mode the user is filling manual validation
  // results row-by-row; the ValidationEditButtons above Status
  // Overview owns the Save/Cancel flow.
  const selectionProps = isEditing
    ? {}
    : {
        selectionType: "multi" as const,
        selectedItems: selectedAsins,
        trackBy: porRowKey,
        isItemDisabled: (item: FullAsinWithAudits | RestrictedAsin) =>
          !isFullAsin(item),
        onSelectionChange: (event: {
          readonly detail: {
            readonly selectedItems: readonly (
              | FullAsinWithAudits
              | RestrictedAsin
            )[];
          };
        }) => {
          const next = event.detail.selectedItems.filter(isFullAsin);
          setSelectedAsins(next);
        },
        ariaLabels: {
          selectionGroupLabel: "Validation Dashboard ASIN selection",
          allItemsSelectionLabel: ({
            selectedItems,
          }: {
            readonly selectedItems: readonly FullAsinWithAudits[];
          }) => `${selectedItems.length} ASINs selected`,
          itemSelectionLabel: (_data: unknown, item: FullAsinWithAudits) =>
            `Select ASIN ${item.asin} in ${item.marketplace}`,
        },
      };

  return (
    <div data-testid="validation-dashboard-asin-table-wrapper">
      <SubmitPassExceptionsModal />
      <SubmitCatalogDefaultsModal />
      <Table
        data-testid="validation-dashboard-asin-table"
        loading={isLoading}
        loadingText={"Loading"}
        empty={EmptyTableState()}
        columnDefinitions={columnDefinitions}
        items={items}
        stickyHeader={true}
        stickyColumns={{ first: 1 }}
        visibleColumns={visibleColumns as string[]}
        {...selectionProps}
        sortingColumn={sort}
        sortingDescending={descending}
        onSortingChange={(event) => {
          const sortingField = event.detail.sortingColumn?.sortingField;
          dispatch(
            setSort({
              sortingColumn:
                sortingField !== undefined ? { sortingField } : undefined,
              isDescending: event.detail.isDescending ?? false,
            })
          );
          dispatch(setValidationPageNumber({ pageNumber: 1 }));
        }}
        header={
          <ValidationDashboardTableHeader
            visibleColumnIds={preferences.visibleContent}
            columnDefinitions={DASHBOARD_COLUMN_DEFINITIONS}
            selectedAsins={selectedAsins}
            isEditing={isEditing}
            editingServiceName={
              selectedValidationService?.serviceName ?? undefined
            }
          />
        }
        pagination={
          isEditing ? undefined : (
            <Pagination
              data-testid="validation-dashboard-asin-pagination"
              pagesCount={pagesCount}
              currentPageIndex={pageNumber}
              disabled={isLoading}
              onChange={({ detail }) =>
                dispatch(
                  setValidationPageNumber({
                    pageNumber: detail.currentPageIndex,
                  })
                )
              }
            />
          )
        }
        preferences={
          isEditing ? undefined : (
            <CollectionPreferences
              data-testid="validation-dashboard-asin-preferences"
              title={"Preferences"}
              confirmLabel={"Confirm"}
              cancelLabel={"Cancel"}
              disabled={isLoading}
              preferences={{
                pageSize: preferences.pageSize,
                visibleContent: preferences.visibleContent as string[],
              }}
              pageSizePreference={pageSelectorOptions}
              visibleContentPreference={asinTableColumnDefinitions}
              onConfirm={onPreferencesConfirm}
            />
          )
        }
        className={"porTable"}
      />
    </div>
  );
};

export default ValidationDashboardTable;
